import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";
import { useI18n } from "@/i18n";

export const scrollToSection = (hash) => {
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -70 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToSection(hash), open ? 150 : 0);
  };

  const links = [
    { id: "#about", label: t.nav.about },
    { id: "#skills", label: t.nav.skills },
    { id: "#projects", label: t.nav.projects },
    { id: "#contact", label: t.nav.contact },
  ];

  const LangSwitch = ({ className = "", prefix = "lang" }) => (
    <div className={`flex items-center gap-1 font-mono text-xs ${className}`}>
      {["en", "it"].map((l) => (
        <button
          key={l}
          data-testid={`${prefix}-switch-${l}`}
          onClick={() => setLang(l)}
          className={`px-2 py-1 tracking-[0.2em] transition-colors duration-200 ${lang === l ? "text-gold" : "text-zinc-500 hover:text-white"}`}
          aria-label={`Switch language to ${l.toUpperCase()}`}
        >
          [{l.toUpperCase()}]
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-[100] border-b border-line bg-black/60 backdrop-blur-xl">
      <div className="mx-auto flex h-[70px] max-w-[1440px] items-center justify-between px-6 md:px-10 xl:px-16">
        <a href="/" data-testid="header-logo" onClick={(e) => { e.preventDefault(); window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-3">
          <Logo size={30} />
          <div className="leading-none">
            <div className="font-display text-[13px] font-bold tracking-wide text-white">CALEB DEMISEW ZELEKE</div>
            <div className="mt-1 text-[10px] tracking-[0.3em] text-zinc-500">PLC // TIA PORTAL</div>
          </div>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-${l.id.slice(1)}`}
              onClick={(e) => go(e, l.id)}
              className="group relative text-xs tracking-[0.25em] text-zinc-400 transition-colors duration-200 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <LangSwitch className="hidden md:flex" />
          <a
            href="#contact"
            data-testid="header-cta"
            onClick={(e) => go(e, "#contact")}
            className="hidden items-center gap-2 border border-gold bg-gold px-4 py-2 text-xs font-bold tracking-[0.2em] text-black transition-transform duration-200 hover:-translate-y-0.5 lg:flex"
          >
            {t.nav.cta}
          </a>
          <button
            data-testid="mobile-menu-btn"
            className="flex h-9 w-9 flex-col items-center justify-center gap-1.5 border border-line lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            <span className={`h-px w-4 bg-white transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
            <span className={`h-px w-4 bg-white transition-transform duration-300 ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden border-t border-line bg-black/90 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <a key={l.id} href={l.id} data-testid={`mobile-nav-${l.id.slice(1)}`} onClick={(e) => go(e, l.id)} className="py-3 text-sm tracking-[0.25em] text-zinc-300 hover:text-gold">
                  {l.label}
                </a>
              ))}
              <div className="flex items-center justify-between pt-3">
                <LangSwitch prefix="menu-lang" />
                <a href="#contact" data-testid="mobile-header-cta" onClick={(e) => go(e, "#contact")} className="border border-gold bg-gold px-4 py-2 text-xs font-bold tracking-[0.2em] text-black">
                  {t.nav.cta}
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
